/** An individual top-level navigation group and its children. */
interface NavGroup {
  /** The top level wrapper element. */
  root: Element;

  /** The top level link element. */
  parentLink: Element | null;

  /** The mega menu element. */
  menu: HTMLElement | null;

  /** The secondary link elements. */
  secondaryLinks: NodeListOf<HTMLElement>;

  /** The tertiary navigation elements. */
  tertiaryNavs: NodeListOf<HTMLElement>;

  /** The highlight elements. */
  highlights: NodeListOf<HTMLElement>;
}

/** Root header element. */
const root: Element | null = document.querySelector(".js-header");

/** Inner header element. */
const inner: Element | null = document.querySelector(".js-header-inner");

/** Navigation element. */
const nav: Element | null = document.querySelector(".js-nav");

/** Navigation toggle elements. */
const navToggles: NodeListOf<Element> =
  document.querySelectorAll(".js-nav-toggle");

/** Back button elements. */
const backButtons: NodeListOf<Element> =
  document.querySelectorAll(".js-nav-back");

/** All navigation groups. */
const navGroups: NavGroup[] = Array.from(
  document.querySelectorAll(".js-nav-group")
).map((root: Element): NavGroup => {
  return {
    root,
    parentLink: root.querySelector(".js-nav-parent-link"),
    menu: root.querySelector(".js-nav-menu"),
    secondaryLinks: root.querySelectorAll(".js-nav-secondary-link"),
    tertiaryNavs: root.querySelectorAll(".js-nav-tertiary"),
    highlights: root.querySelectorAll(".js-nav-highlight"),
  };
});

/** Is the mobile navigation open? */
const isMobileNavActive = (): boolean =>
  document.body.classList.contains("is-nav-active");

/** Is Getting Healthy campaign? */
const isGettingHealthy = (): boolean => document.body.classList.contains("gh");
const isGettingHealthy2 = (): boolean =>
  document.body.classList.contains("gh2");

/** Toggle the nested nav active state. */
const nestedNavActive = (active: boolean) =>
  document.body.classList.toggle(
    "is-nested-nav-active",
    active && isMobileNavActive()
  );

/** Close an individual navigation group. */
const closeNavGroup = (navGroup: NavGroup): void => {
  nestedNavActive(false);
  navGroup.root.classList.remove("is-active");
  updateActiveSecondaryLink(navGroup, null);
  navGroup.tertiaryNavs.forEach((tertiaryNav) =>
    closeTertiaryNav(navGroup, tertiaryNav)
  );

  // Reset the menu layout styles. Useful if user switches between desktop and
  // mobile navigation.
  if (navGroup.menu) {
    navGroup.menu.style.left = "";
    navGroup.menu.style.width = "";
  }
};

/** Open an individual navigation group. */
const openNavGroup = (navGroup: NavGroup): void => {
  nestedNavActive(true);
  navGroup.root.classList.add("is-active");
  Array.from(navGroup.highlights)
    .filter(isTopLevelHighlight)
    .map(openHighlight);

  if (navGroup.menu && isMobileNavActive()) {
    navGroup.menu.style.left = "";
    navGroup.menu.style.width = "";
  } else if (navGroup.menu) {
    const offset =
      (nav?.getBoundingClientRect().left || 0) -
      (inner?.getBoundingClientRect().left || 0);

    navGroup.menu.style.left = `${-1 * offset}px`;
    navGroup.menu.style.width = `${inner?.clientWidth}px`;
  }
};

/** Setup events for an individual navigation group. */
const attachNavGroup = (navGroup: NavGroup): void => {
  ["mouseenter", "focus"].forEach((event) =>
    navGroup.parentLink?.addEventListener(event, () => {
      if (isMobileNavActive()) {
        return;
      }

      navGroups.filter((target) => target !== navGroup).forEach(closeNavGroup);
      openNavGroup(navGroup);
    })
  );

  navGroup.parentLink?.addEventListener("click", (event) => {
    if (isMobileNavActive() && navGroup.menu) {
      event.preventDefault();
      openNavGroup(navGroup);
    }
  });

  navGroup.secondaryLinks.forEach((secondaryLink) =>
    attachSecondaryLink(navGroup, secondaryLink)
  );
};

/** Check if a nav group (its root element), tertiary navigation, or higlight is open. */
const isOpen = (nav: Element): boolean => nav.classList.contains("is-active");

/** Close a tertiary navigation. */
const closeTertiaryNav = (navGroup: NavGroup, tertiaryNav: HTMLElement) => {
  nestedNavActive(false);
  updateActiveSecondaryLink(navGroup, null);
  tertiaryNav.classList.remove("is-active");
  Array.from(navGroup.highlights).map(closeHighlight);
};

/** Open a tertiary navigation. */
const openTertiaryNav = (navGroup: NavGroup, tertiaryNav: HTMLElement) => {
  nestedNavActive(true);
  updateActiveSecondaryLink(navGroup, tertiaryNav);
  tertiaryNav.classList.add("is-active");
  Array.from(navGroup.highlights)
    .filter(
      (target) =>
        target.dataset.levelId === tertiaryNav.dataset.levelId ||
        isTopLevelHighlight(target)
    )
    .sort((target) => (isTopLevelHighlight(target) ? 1 : -1))
    .slice(0, 1)
    .map(openHighlight);
};

/** Set the active secondary link when updating a tertiary navigation. */
const updateActiveSecondaryLink = (
  navGroup: NavGroup,
  tertiaryNav: HTMLElement | null
) =>
  Array.from(navGroup.secondaryLinks).map((secondaryLink) =>
    secondaryLink.classList.toggle(
      "is-active",
      tertiaryNav !== null &&
        secondaryLink.dataset.levelId === tertiaryNav.dataset.levelId
    )
  );

/** Setup events for a secondary link inside a navigation group. */
const attachSecondaryLink = (
  navGroup: NavGroup,
  secondaryLink: HTMLElement
) => {
  const getTertiaryNavFromSecondaryLink = (
    navGroup: NavGroup,
    secondaryLink: HTMLElement
  ): HTMLElement | null =>
    Array.from(navGroup.tertiaryNavs).filter(
      (target) => target.dataset.levelId === secondaryLink.dataset.levelId
    )[0];

  secondaryLink.addEventListener("click", (event) => {
    const tertiaryNav = getTertiaryNavFromSecondaryLink(
      navGroup,
      secondaryLink
    );

    if (isMobileNavActive() && tertiaryNav) {
      event.preventDefault();
      openTertiaryNav(navGroup, tertiaryNav);
    }
  });

  secondaryLink.addEventListener("mouseenter", (_event) => {
    if (isMobileNavActive()) {
      return;
    }

    const tertiaryNav = getTertiaryNavFromSecondaryLink(
      navGroup,
      secondaryLink
    );

    Array.from(navGroup.tertiaryNavs)
      .filter((target) => target !== tertiaryNav)
      .forEach((tertiaryNav) => closeTertiaryNav(navGroup, tertiaryNav));

    if (!(tertiaryNav instanceof HTMLElement)) {
      return;
    }

    openTertiaryNav(navGroup, tertiaryNav);
  });
};

/** Setup events for navigation toggles. */
const attachNavToggle = (navToggle: Element): void => {
  if (window.innerWidth < 1024) {
    nav?.setAttribute("aria-hidden", "true");
  }

  navToggle.addEventListener("click", () => {
    if (document.body.classList.toggle("is-nav-active")) {
      if (!isGettingHealthy) {
        window.scrollTo({
          top: 0,
          left: 0,
        });
      }

      nav?.setAttribute("aria-hidden", "false");

      navToggles.forEach((navToggle) =>
        navToggle.setAttribute("aria-expanded", "true")
      );
    } else {
      if (isGettingHealthy2()) {
        const header = document.querySelector(".header--gh2");
        const parentLinks = header?.querySelectorAll(".js-nav-parent-link");
        parentLinks?.forEach((link) => {
          const parentEl = link.parentElement as HTMLElement;
          link.classList.remove("is-open");
          const children = parentEl?.querySelector(
            ".header__navigation--children"
          );
          children?.classList.remove("open");
        });
      }
      navGroups.forEach(closeNavGroup);

      nav?.setAttribute("aria-hidden", "true");

      navToggles.forEach((navToggle) =>
        navToggle.setAttribute("aria-expanded", "false")
      );
    }
  });
};

/**
 * Close the first open tertiary navigation or the first open navigation
 * group/secondary navigation.
 */
const closeDeepestLevel = (): void => {
  const openTertiaryNavNavGroup = navGroups.filter(
    (navGroup) => Array.from(navGroup.tertiaryNavs).filter(isOpen).length >= 1
  )[0];

  if (openTertiaryNavNavGroup) {
    const openTertiaryNav = Array.from(
      openTertiaryNavNavGroup.tertiaryNavs
    ).filter(isOpen)[0];

    closeTertiaryNav(openTertiaryNavNavGroup, openTertiaryNav);
    openNavGroup(openTertiaryNavNavGroup);

    return;
  }

  const openedNavGroup = navGroups.filter((navGroup) =>
    isOpen(navGroup.root)
  )[0];

  if (openedNavGroup) {
    closeNavGroup(openedNavGroup);
  }

  return;
};

// enable second level nav accordion dropdowns
const initGhParentLinks = (): void => {
  if (!isGettingHealthy2()) return;
  const header = document.querySelector(".header--gh2");
  const parentLinks = header?.querySelectorAll(".js-nav-parent-link");
  parentLinks?.forEach((link) => {
    link.addEventListener("click", () => {
      const parentEl = link.parentElement as HTMLElement;
      link.classList.toggle("is-open");
      const children = parentEl?.querySelector(".header__navigation--children");
      children?.classList.toggle("open");
    });
  });
};

/** Open a highlight. */
const openHighlight = (highlight: Element) =>
  highlight.classList.add("is-active");

/** Close a highlight. */
const closeHighlight = (highlight: Element) =>
  highlight.classList.remove("is-active");

/** Is a highlight for the top level of the nav group? */
const isTopLevelHighlight = (highlight: HTMLElement) =>
  (highlight.dataset.levelId?.length || 0) === 0;

/** Attach navigation back button. */
const attachBackButton = (button: Element): void =>
  button.addEventListener("click", closeDeepestLevel);

/** Close any open navigation groups during exit intent. */
inner?.addEventListener("mouseleave", () => {
  if (isMobileNavActive()) {
    return;
  }

  navGroups.forEach(closeNavGroup);
});

/** Close mobile navigation if screen is now large enough. */
window.addEventListener("resize", () => {
  if (window.innerWidth >= 1024) {
    document.body.classList.remove("is-nav-active", "is-nested-nav-active");
    navGroups.forEach(closeNavGroup);
  }
});

navGroups.forEach(attachNavGroup);
navToggles.forEach(attachNavToggle);
backButtons.forEach(attachBackButton);
initGhParentLinks();
